/**
 * `/story/:storyId` — the public story. Works in a fresh browser with no fan: GET /stories/:id,
 * then the same vertical player with the persisted public speech, the
 * author's verdict over the verified final, their chosen take, and a response CTA.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { DirectorLanguage, DirectorShow, PersonalStory } from '@clutch/core';
import { ENERGY_COPY, STYLE_COPY, showFromProfile } from '../director/show';
import { useClient } from '../client';
import { navigate } from '../router';
import { teamAccent } from '../ui/color';
import { createDirectorApi, DirectorUnavailableError, requireModelShow } from '../director/api';
import { DirectorUnavailable } from '../director/Unavailable';
import { MissingStoryTranslation, StoryPlayer, sceneFor } from '../director/StoryPlayer';
import { VerdictCard } from '../director/VerdictCard';
import { verdictFor } from '../director/perspective';
import { forgetStory, ownStories } from '../director/PublishSheet';
import { strings } from '../director/strings';
import { htmlLang, pick, storyHasTranslation } from '../director/i18n';
import { Headshot } from '../ui/Media';
import { MomentVisual } from '../director/MomentVisual';
import { momentReason, momentVisual } from '../director/moment-presentation';

const COPY = {
  en: { cta: 'Think you’d have called it?', ctaSub: 'Make your call on the next game.', ctaBtn: 'Make your call', remove: 'Remove story', removing: 'Removing…', removeFailed: 'Couldn’t remove the story. Try again.', removed: 'This story was removed by its author.', missing: 'This story isn’t available.', loading: 'Opening the story…', by: 'A Clutch story by', take: 'Their reaction', start: 'Watch the story' },
  hi: { cta: 'क्या आप इसे पहले भांप लेते?', ctaSub: 'अगले मैच पर अपनी कॉल लगाइए।', ctaBtn: 'अपनी कॉल लगाइए', remove: 'स्टोरी हटाएँ', removing: 'हटा रहे हैं…', removeFailed: 'स्टोरी हट नहीं सकी। फिर कोशिश करें।', removed: 'लेखक ने यह स्टोरी हटा दी।', missing: 'यह स्टोरी उपलब्ध नहीं है।', loading: 'स्टोरी खुल रही है…', by: 'Clutch स्टोरी —', take: 'उनका रिएक्शन', start: 'स्टोरी देखें' },
  'zh-Hans': { cta: '换作你，会预测对吗？', ctaSub: '在下一场比赛做出你的预测。', ctaBtn: '做出你的预测', remove: '删除故事', removing: '正在删除…', removeFailed: '删除失败，请重试。', removed: '作者已删除这个故事。', missing: '此故事不可用。', loading: '正在打开故事…', by: 'Clutch故事，作者', take: '作者的反应', start: '观看故事' },
};

export function PublicStory({ storyId }: { storyId: string }) {
  const client = useClient();
  const api = useMemo(() => client?.director ?? createDirectorApi('/api'), [client]);
  const [story, setStory] = useState<PersonalStory | null>();
  const [removed, setRemoved] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(false);
  const [started, setStarted] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const owner = ownStories()[storyId];
  const [voiceName, setVoiceName] = useState<string>();
  // The author's directed show (when the server stored it): credit its style and chosen voice.
  const authored = (story?.director as { show?: Partial<DirectorShow> & { voiceName?: string } } | undefined)?.show;
  useEffect(() => {
    if (authored?.voiceName) return setVoiceName(authored.voiceName); // recorded by the server at generation
    const id = authored?.voiceId;
    if (!id || !api.voices) return;
    let alive = true;
    void api.voices().then((v) => { if (alive) setVoiceName(v?.find((x) => x.voiceId === id)?.name); });
    return () => { alive = false; };
  }, [api, authored?.voiceId]);

  useEffect(() => {
    let alive = true;
    setFetchError(false);
    setStory(undefined);
    void (async () => {
      try {
        let loaded: PersonalStory | undefined;
        try { loaded = await api.story(storyId, owner); }
        catch (error) {
          // Stale local ownership cannot prevent an otherwise public link from opening.
          if (!owner) throw error;
          loaded = await api.story(storyId);
        }
        if (alive) setStory(loaded ?? null);
      } catch { if (alive) setFetchError(true); }
    })();
    return () => { alive = false; };
  }, [api, storyId, owner, attempt]);

  const l = story?.director.language ?? 'en';
  const c = COPY[l];
  if (fetchError) return <main className="story story--state" data-testid="public-story" data-phase="error" lang={htmlLang(l)}>
    <p role="alert">{pick({ en: 'The story could not load. Try again.', hi: 'स्टोरी लोड नहीं हुई। फिर कोशिश करें।', zh: '故事加载失败，请重试。' }, l)}</p>
    <button className="btn btn--primary" onClick={() => setAttempt((n) => n + 1)} data-testid="story-load-retry">{strings(l).retry}</button>
  </main>;
  if (story === undefined) return <main className="story story--state" role="status" data-testid="public-story" data-phase="loading"><span className="cu__orb" aria-hidden="true" /><p>{c.loading}</p></main>;
  if (story === null || removed)
    return (
      <main className="story story--state" data-testid="public-story" data-phase="missing">
        <p className="story__missing">{removed ? c.removed : c.missing}</p>
        <Cta c={c} />
      </main>
    );

  const d = story.director;
  if (d.generation.mode === 'model' || d.showPlan) {
    try { requireModelShow(d); }
    catch (error) {
      if (error instanceof DirectorUnavailableError) return <main className="story story--state" lang={htmlLang(l)}>
        <DirectorUnavailable error={error.detail} language={l} onRetry={() => setAttempt((n) => n + 1)} onBack={() => navigate('/')} />
      </main>;
      throw error;
    }
  }
  if (!storyHasTranslation(d)) return <MissingStoryTranslation language={l} onClose={() => navigate('/')} />;
  const author = story.author.displayName.split(/\s+/)[0] ?? story.author.displayName;
  const observer = d.audience?.kind === 'public';
  const verdict = verdictFor({ director: d, language: l, ...(observer ? { who: author } : {}) });
  const style = { '--a': teamAccent(d.game.away.tricode), '--h': teamAccent(d.game.home.tricode) } as CSSProperties;
  const remove = async () => {
    if (!owner || removing) return;
    setRemoving(true);
    setRemoveError(false);
    try {
      await api.removeStory(storyId, owner);
      forgetStory(storyId);
      setRemoved(true);
    } catch {
      setRemoving(false);
      setRemoveError(true);
    }
  };
  const t = strings(l);
  const show = authored ? showFromProfile({ show: authored }) : undefined;
  const credit = show ? <><b>{pick({ en: `Directed by ${author}`, hi: `${author} के निर्देशन में`, zh: `${author}执导` }, l)}</b><span>{pick(STYLE_COPY[show.style].name, l)}</span><span>{pick(ENERGY_COPY[show.energy], l)}</span>{voiceName && <span>{voiceName}</span>}</> : undefined;

  return (
    <main className="story" style={style} data-testid="public-story" data-phase="ready" lang={htmlLang(l)}>
      {!started ? (
        <section className="story__cover">
          <p className="story__by">{c.by} <b>{author}</b></p>
          <p className="story__label"><i aria-hidden="true" />{t.replayLabel}</p>
          {credit && <p className="sp__credit" data-testid="story-credit">{credit}</p>}
          <VerdictCard verdict={verdict} director={d} language={l} {...(observer ? { who: author } : {})} />
          {story.take && <TakeClip url={api.media(story.take.url)} label={c.take} language={l} />}
          <button className="btn btn--primary btn--block story__play" onClick={() => setStarted(true)} data-testid="story-start">▶ {c.start}</button>
          <p className="story__told">{pick({ en: `${author}’s catch-up, as Clutch told it`, hi: `${author} का कैच-अप, जैसा Clutch ने सुनाया`, zh: `Clutch为${author}讲述的比赛回顾` }, l)}</p>
          <ol className="story__moments" data-testid="story-moments">
            {d.segments.map((seg, i) => {
              const sc = sceneFor(d, i, l);
              const visual = momentVisual(d, sc.moment);
              const reason = momentReason(d, sc.moment, l);
              return (
                <li key={seg.segmentId} className={`story__moment story__moment--${sc.kind}`} data-testid="story-moment">
                  <p className="story__moment-top"><span>{sc.eyebrow}</span>{sc.clock && <b>{sc.clock}</b>}{sc.home !== undefined && <strong>{d.game.away.tricode} {sc.away} – {sc.home} {d.game.home.tricode}</strong>}</p>
                  <h3>{sc.title}</h3>
                  {sc.moment?.player && <div className="story__moment-player">
                    <Headshot personId={sc.moment.player.personId} name={sc.moment.player.name} source={d.game.source} size={56} />
                    <b>{sc.moment.player.name}</b>
                  </div>}
                  {visual && <MomentVisual visual={visual} language={l} />}
                  {reason && <p className="sp__reason"><i aria-hidden="true" />{reason}</p>}
                  <p>{seg.text}</p>
                </li>
              );
            })}
          </ol>
          <Cta c={c} />
          {removeError && <p className="alert" role="alert">{c.removeFailed}</p>}
          {owner && <button className="story__remove" onClick={remove} disabled={removing} data-testid="remove-story">{removing ? c.removing : c.remove}</button>}
        </section>
      ) : (
        <StoryPlayer
          director={d}
          requireAudio={d.generation.mode === 'model' || !!d.showPlan}
          listenOnStart={d.generation.mode === 'model' || !!d.showPlan}
          live={false}
          loadSpeech={() => api.storySpeech(storyId, observer ? undefined : owner)}
          mediaUrl={api.media}
          onClose={() => setStarted(false)}
          {...(credit ? { credit } : {})}
          end={<>
            <VerdictCard verdict={verdict} director={d} language={l} {...(observer ? { who: author } : {})} />
            {story.take && <TakeClip url={api.media(story.take.url)} label={c.take} language={l} />}
            <Cta c={c} />
          </>}
        />
      )}
    </main>
  );
}

function TakeClip({ url, label, language }: { url: string; label: string; language: DirectorLanguage }) {
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    setPhase('loading');
    const timeout = setTimeout(() => setPhase((current) => current === 'loading' ? 'error' : current), 15_000);
    return () => clearTimeout(timeout);
  }, [url, attempt]);
  return (
    <figure className={`story__take story__take--${phase}`} data-testid="story-take" data-phase={phase}>
      {phase !== 'error' && <video key={`${url}:${attempt}`} src={url} playsInline controls preload="metadata"
        aria-label={label} onLoadedMetadata={() => setPhase('ready')} onError={() => setPhase('error')} />}
      {phase !== 'ready' && <div className="story__take-status" role="status">
        <span aria-hidden="true">{phase === 'loading' ? '◌' : '↻'}</span>
        <p>{pick({ hi: phase === 'loading' ? 'रिएक्शन खुल रहा है…' : 'यह रिएक्शन अभी नहीं चल सका। कहानी नीचे देख सकते हैं।',
          en: phase === 'loading' ? 'Opening their reaction…' : 'This reaction couldn’t play. You can still watch the story.',
          zh: phase === 'loading' ? '正在打开作者的反应…' : '这段录像暂时无法播放。你仍可观看故事。' }, language)}</p>
        {phase === 'error' && <button className="btn btn--ghost" onClick={() => setAttempt((n) => n + 1)} data-testid="story-take-retry">{strings(language).retry}</button>}
      </div>}
      <figcaption>{label}</figcaption>
    </figure>
  );
}

function Cta({ c }: { c: (typeof COPY)['en'] }) {
  return (
    <div className="story__cta" data-testid="story-cta">
      <b>{c.cta}</b>
      <span>{c.ctaSub}</span>
      <button className="btn btn--primary" onClick={() => navigate('/')} data-testid="story-make-call">{c.ctaBtn} →</button>
    </div>
  );
}
